import React, { useEffect, useState } from 'react';
import relatorioService, { type PrestacaoRemuneracaoEquipe } from '../../services/relatorioPrestacaoContasService';
import DownloadButtons from './DownloadButtons';
import { toUpperText } from '../../utils/formatters';
import {
  bodyTextClass, dangerTextButtonClass, formatCurrency, formatCurrencyInput, inputClass,
  labelTextClass, parseCurrencyInput, primaryButtonClass, strongTextClass,
  subtleSurfaceClass, surfaceClass, type SectionProps,
} from './shared';

const Anexo8RemuneracaoEquipeSection: React.FC<SectionProps> = ({ pcId, podeEditar }) => {
  const [itens, setItens] = useState<PrestacaoRemuneracaoEquipe[]>([]);
  const [form, setForm] = useState({ funcaoCargo: '', valorRemuneracao: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const carregar = async () => {
    setLoading(true);
    setError(null);
    try {
      setItens(await relatorioService.getRemuneracoes(pcId));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao carregar a remuneração da equipe.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcId]);

  const adicionar = async () => {
    if (!form.funcaoCargo) {
      setError('Informe a função/cargo do profissional.');
      return;
    }
    setSaving('novo');
    setError(null);
    try {
      await relatorioService.criarRemuneracao(pcId, {
        funcaoCargo: form.funcaoCargo,
        valorRemuneracao: form.valorRemuneracao ? parseCurrencyInput(form.valorRemuneracao) : null,
      });
      setForm({ funcaoCargo: '', valorRemuneracao: '' });
      await carregar();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao adicionar remuneração.');
    } finally {
      setSaving(null);
    }
  };

  const excluir = async (id: string) => {
    if (!confirm('Remover este registro de remuneração?')) return;
    setSaving(id);
    try {
      await relatorioService.excluirRemuneracao(id);
      await carregar();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao remover a remuneração.');
    } finally {
      setSaving(null);
    }
  };

  const total = itens.reduce((sum, i) => sum + Number(i.valorRemuneracao || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className={`text-lg font-semibold ${strongTextClass}`}>Anexo VIII · Relatório de Publicidade</h2>
        <DownloadButtons pcId={pcId} anexo="8" nomeArquivo="anexo-viii-publicidade" />
      </div>

      <p className={`text-sm ${labelTextClass}`}>
        Preencha aqui apenas a remuneração da equipe de trabalho, quando paga com recursos da parceria. Valores,
        metas executadas e bens adquiridos vêm automaticamente dos demais anexos.
      </p>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      {podeEditar && (
        <div className={`flex flex-wrap items-end gap-2 rounded-2xl border p-4 ${subtleSurfaceClass}`}>
          <div className="flex-1 min-w-[200px]">
            <label className={`block text-xs font-medium ${labelTextClass}`}>Função/Cargo</label>
            <input className={inputClass} value={form.funcaoCargo} onChange={(e) => setForm((p) => ({ ...p, funcaoCargo: toUpperText(e.target.value) }))} />
          </div>
          <div className="w-40">
            <label className={`block text-xs font-medium ${labelTextClass}`}>Remuneração</label>
            <input type="text" inputMode="numeric" placeholder="R$ 0,00" className={inputClass} value={form.valorRemuneracao}
              onChange={(e) => setForm((p) => ({ ...p, valorRemuneracao: formatCurrencyInput(e.target.value) }))} />
          </div>
          <button type="button" onClick={adicionar} disabled={saving === 'novo'}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${primaryButtonClass}`}>
            {saving === 'novo' ? 'Salvando...' : 'Adicionar'}
          </button>
        </div>
      )}

      {loading ? (
        <p className={`text-sm ${bodyTextClass}`}>Carregando...</p>
      ) : itens.length === 0 ? (
        <p className={`text-sm ${labelTextClass}`}>Equipe não remunerada com recursos da parceria.</p>
      ) : (
        <div className={`overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <table className="w-full text-sm">
            <thead className={subtleSurfaceClass}>
              <tr>
                <th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>Função/Cargo</th>
                <th className={`px-4 py-2 text-right font-semibold ${labelTextClass}`}>Remuneração</th>
                <th className={`px-4 py-2 ${labelTextClass}`}></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => (
                <tr key={i.id} className="border-t border-slate-200 dark:border-slate-800">
                  <td className={`px-4 py-2 ${strongTextClass}`}>{i.funcaoCargo}</td>
                  <td className={`px-4 py-2 text-right ${bodyTextClass}`}>{formatCurrency(i.valorRemuneracao)}</td>
                  <td className="px-4 py-2 text-right">
                    {podeEditar && (
                      <button type="button" onClick={() => excluir(i.id)} disabled={saving === i.id} className={dangerTextButtonClass}>
                        Excluir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className={`border-t border-slate-300 font-semibold dark:border-slate-700 ${subtleSurfaceClass}`}>
                <td className={`px-4 py-2 ${strongTextClass}`}>Total</td>
                <td className={`px-4 py-2 text-right ${strongTextClass}`}>{formatCurrency(total)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Anexo8RemuneracaoEquipeSection;
